import './faq-accordion.scss';
import Base from '../base/base';
import gsap from 'gsap';

interface FaqAccordionOptions {
  containerSelector?: string;
  listSelector?: string;
  itemSelector?: string;
  headerSelector?: string;
  bodySelector?: string;
  iconSelector?: string;
  openClass?: string;
  /** Added to each container once the lists are wired, arming the stylesheet */
  readyClass?: string;
  /** Background the icon animates to when open; null leaves the colour alone */
  iconOpenBackground?: string | null;
  /** Background the icon returns to when closed; null leaves the colour alone */
  iconClosedBackground?: string | null;
  animationDuration?: number;
  ease?: string;
  debug?: boolean;
  [key: string]: any;
}

interface FaqListListener {
  element: HTMLElement;
  handler: EventListener;
}

export default class FaqAccordion extends Base {
  private config: Required<Omit<FaqAccordionOptions, 'debug'>>;
  private listListeners: FaqListListener[] = [];
  private containers: HTMLElement[] = [];
  private resizeHandler: (() => void) | null = null;

  constructor(options: FaqAccordionOptions = {}) {
    super(options.debug || false);

    this.config = {
      containerSelector: '.faq-section',
      listSelector: '.faq-list',
      itemSelector: '.faq-item',
      headerSelector: '.faq-header',
      bodySelector: '.faq-body',
      iconSelector: '.faq-icon-wrap',
      openClass: 'is-open',
      readyClass: 'is-ready',
      iconOpenBackground: 'var(--soft-red)',
      iconClosedBackground: 'var(--deep-green)',
      animationDuration: 0.5,
      ease: 'power4.out',
      ...options
    };

    this.init();
  }

  init(): void {
    const containers = document.querySelectorAll<HTMLElement>(this.config.containerSelector);

    if (!containers.length) {
      this.log('FaqAccordion: no containers found');
      return;
    }

    containers.forEach((container) => {
      const lists = container.querySelectorAll<HTMLElement>(this.config.listSelector);
      if (!lists.length) return;

      lists.forEach((list) => {
        this.setupInitialState(list);
        const handler: EventListener = (event: Event) => this.handleListClick(event, list);
        list.addEventListener('click', handler);
        this.listListeners.push({ element: list, handler });
      });

      // The stylesheet hides the answers only from here on, so markup the
      // component never reached keeps whatever the export gave it.
      container.classList.add(this.config.readyClass);
      this.containers.push(container);
    });

    this.resizeHandler = () => this.refreshOpenHeights();
    window.addEventListener('resize', this.resizeHandler);

    this.log('FaqAccordion initialized');
  }

  private setupInitialState(list: HTMLElement): void {
    const items = list.querySelectorAll<HTMLElement>(this.config.itemSelector);
    items.forEach((item) => {
      item.classList.remove(this.config.openClass);
      const body = item.querySelector<HTMLElement>(this.config.bodySelector);
      if (body) {
        // Webflow can ship the open class on a body from the Designer's own
        // editing state; the item is the only place this component reads it.
        body.classList.remove(this.config.openClass);
        gsap.set(body, { height: 0, overflow: 'hidden' });
      }

      const icon = item.querySelector<HTMLElement>(this.config.iconSelector);
      if (icon) {
        gsap.set(icon, { rotate: 0 });
      }
    });
  }

  private handleListClick(event: Event, list: HTMLElement): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.closest(this.config.bodySelector)) return;

    const item = target.closest(this.config.itemSelector) as HTMLElement | null;
    if (!item || !list.contains(item)) return;

    if (item.classList.contains(this.config.openClass)) {
      this.closeItem(item);
      return;
    }

    this.closeOtherItems(list, item);
    this.openItem(item);
  }

  private openItem(item: HTMLElement): void {
    const body = item.querySelector<HTMLElement>(this.config.bodySelector);
    const icon = item.querySelector<HTMLElement>(this.config.iconSelector);

    if (!body) return;

    item.classList.add(this.config.openClass);

    gsap.to(body, {
      height: body.scrollHeight,
      duration: this.config.animationDuration,
      ease: this.config.ease,
      onComplete: () => {
        //gsap.set(body, { clearProps: 'height' });
      }
    });

    if (icon) {
      gsap.to(icon, {
        rotate: 45,
        ...(this.config.iconOpenBackground ? { backgroundColor: this.config.iconOpenBackground } : {}),
        duration: this.config.animationDuration,
        ease: this.config.ease
      });
    }
  }

  private closeItem(item: HTMLElement): void {
    const body = item.querySelector<HTMLElement>(this.config.bodySelector);
    const icon = item.querySelector<HTMLElement>(this.config.iconSelector);

    if (!body) return;

    item.classList.remove(this.config.openClass);

    gsap.set(body, { height: body.offsetHeight, overflow: 'hidden' });
    gsap.to(body, {
      height: 0,
      duration: this.config.animationDuration,
      ease: this.config.ease,
      onComplete: () => {
        gsap.set(body, { height: 0 });
      }
    });

    if (icon) {
      gsap.to(icon, {
        rotate: 0,
        ...(this.config.iconClosedBackground ? { backgroundColor: this.config.iconClosedBackground } : {}),
        duration: this.config.animationDuration,
        ease: this.config.ease
      });
    }
  }

  private closeOtherItems(list: HTMLElement, currentItem: HTMLElement): void {
    const items = Array.from(list.querySelectorAll<HTMLElement>(this.config.itemSelector));
    items.forEach((item) => {
      if (item !== currentItem && item.classList.contains(this.config.openClass)) {
        this.closeItem(item);
      }
    });
  }

  private refreshOpenHeights(): void {
    this.listListeners.forEach(({ element: list }) => {
      const openItems = list.querySelectorAll<HTMLElement>(
        `${this.config.itemSelector}.${this.config.openClass}`
      );

      openItems.forEach((item) => {
        const body = item.querySelector<HTMLElement>(this.config.bodySelector);
        if (!body) return;

        gsap.set(body, { height: body.scrollHeight });
      });
    });
  }

  destroy(): void {
    this.listListeners.forEach(({ element, handler }) => {
      element.removeEventListener('click', handler);
    });

    this.listListeners = [];

    this.containers.forEach((container) => container.classList.remove(this.config.readyClass));
    this.containers = [];

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    this.log('FaqAccordion destroyed');
  }
}
